import { lazy, Suspense, useMemo, useState } from 'react';
import { useAuth } from './firebase/AuthProvider';
import { configProblem } from './firebase/config';
import { useOrgData } from './engine/useOrgData';
import { buildLadder } from './engine/ladder';
import { trainingRole } from '@core/shared/stages';
import { useOrgName } from './engine/useOrgName';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase/config';
import { describeWriteError } from './firebase/writeErrors';
import { pickVariant } from './engine/variants';
import type { Rung } from './engine/ladder';
import { SignIn } from './components/SignIn';
import { ErrorBoundary } from './components/ErrorBoundary';
import { StageLadder } from './components/StageLadder';
import { DrillRunner } from './components/DrillRunner';
import { KnowledgeStage } from './components/KnowledgeStage';
import { SimulationRunner } from './components/SimulationRunner';
import { SummativeGate } from './components/SummativeGate';
import { TrackPicker } from './components/TrackPicker';

// Only managers need the dashboard and the charting library it pulls in, so reps
// never download either.
const ManagerApp = lazy(() => import('./manager/ManagerApp').then((m) => ({ default: m.ManagerApp })));

export function App() {
  const { session, loading: authLoading, signOutNow } = useAuth();
  const { config, user, userMissing, content, loading, error } = useOrgData(
    session?.orgId ?? null,
    session?.user.uid ?? null
  );

  // §22.2 — React state only. Nothing about a session touches browser storage,
  // so there is no route to restore and no progress cached off the server.
  const [openRung, setOpenRung] = useState<Rung | null>(null);
  /**
   * Which check is open, if any. A rung has one; an advanced track has one per
   * unit (§6.12), so the id has to travel rather than being read off the rung.
   */
  const [inCheck, setInCheck] = useState<string | null>(null);
  // §14.3 — which variant a certification attempt serves is decided at the gate,
  // not by the ladder, so a rep cannot see it coming. Practice picks one when the
  // rung is opened: unlimited attempts against a single memorised call teach the
  // call rather than the job.
  const [variantId, setVariantId] = useState<string | null>(null);
  // §13.4 — a manager or admin can step out of the dashboard and walk the same
  // ladder a rep walks. It is a rehearsal: same content, same scoring, no
  // certification (the scoring trigger decides that from the custom claim).
  const [training, setTraining] = useState(false);
  // §7 — which company an admin is looking at. Their own until they switch, and
  // there is nothing to switch for anybody who reaches only one.
  const [viewOrgId, setViewOrgId] = useState<string | null>(null);
  // §7 — a rep can be moved between the client's companies, so which one they
  // are in is something they should be able to read rather than infer.
  const orgName = useOrgName(session?.orgId ?? null);
  const [pickingTrack, setPickingTrack] = useState(false);
  // Which track is mid-save, and why the last one failed. Both live here rather
  // than inside the picker because the picker is unmounted the moment the write
  // lands locally — see chooseTrack below.
  const [savingTrack, setSavingTrack] = useState<string | null>(null);
  const [trackError, setTrackError] = useState<string | null>(null);
  // §14.2 — the gate also chooses the seed, because the persona exclusion has to
  // be decided before the attempt starts and stored with it.
  const [seed, setSeed] = useState<number | undefined>(undefined);

  /**
   * §13.4 — anybody rehearsing walks the ladder unlocked.
   *
   * The gating is most of the product for a rep: each stage teaches what the
   * next one assumes, and skipping is the thing it exists to prevent. It is an
   * obstacle for somebody whose job is to check that the content is right, who
   * would otherwise pass six stages to read the seventh.
   *
   * `rehearsing` is deliberately the one predicate for both things rather than
   * two that happen to agree today: whoever earns no certification from an
   * attempt is exactly whoever may take them in any order, and those two should
   * not be able to drift apart.
   *
   * Only the offer changes. `certifies()` reads the app role from the custom
   * claim and answers no for anybody who is not a rep, so a manager or admin
   * can open the certification call out of order and still cannot be certified
   * by it.
   */
  const rehearsing = session != null && session.appRole !== 'rep';

  const rungs = useMemo(
    () => (config ? buildLadder(config, user, content, new Date(), rehearsing) : []),
    [config, user, content, rehearsing]
  );

  // §3 — a build that was never given a project to talk to. Said plainly, at
  // the front, because the alternative is a sign-in that fails with
  // `auth/api-key-not-valid` and sends whoever is debugging it looking at Auth.
  if (configProblem) {
    return (
      <main className="centered">
        <div className="card">
          <h1>Not configured</h1>
          <p className="error">{configProblem}</p>
          <p className="muted">
            The values are inlined when the app is built, so this is fixed in
            <code> web/apphosting.yaml</code> and deployed again — not in the
            console. Copy them from Firebase console → Project settings → Your
            apps.
          </p>
        </div>
      </main>
    );
  }

  if (authLoading) return <main className="centered"><p className="muted">Loading…</p></main>;
  if (!session) return <SignIn />;


  if (rehearsing && !training) {
    return (
      // Suspense does not catch errors, only pending promises. The dashboard is
      // a separate chunk, so a browser holding an older app shell asks for a
      // file the server has replaced, the import rejects, and without a
      // boundary here that reject unmounts the whole app and leaves a black
      // page. Its own boundary keeps the failure to the dashboard and names it.
      <ErrorBoundary area="dashboard">
        <Suspense fallback={<main className="centered"><p className="muted">Loading…</p></main>}>
          <ManagerApp
            orgId={viewOrgId ?? session.orgId}
            appRole={session.appRole}
            uid={session.user.uid}
            adminOrgIds={session.adminOrgIds}
            onSwitchOrg={setViewOrgId}
            onSignOut={signOutNow}
            onOpenTraining={() => setTraining(true)}
          />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (loading || !config) {
    return <main className="centered"><p className="muted">{error ?? 'Loading…'}</p></main>;
  }

  // A rehearsal needs a track before it can show anything: every simulation in
  // a pack is authored for a setter or a closer, and a manager has no salesRole
  // to answer with. Asked once, changeable whenever.
  /**
   * Choosing a track, and why this is not inside the picker.
   *
   * Firestore applies a write locally before the server has acknowledged it, so
   * the user document gains `trainingTrack` immediately and the condition below
   * goes false — the ladder appears and the picker unmounts. If the server then
   * refuses the write, Firestore rolls it back, the condition goes true again
   * and a *fresh* picker mounts. The screen flashes forward and back, and the
   * error, which lived in the unmounted component, is thrown away with it.
   *
   * `savingTrack` holds the picker on screen across that window, so the failure
   * has somewhere to land and something to say.
   */
  const chooseTrack = async (track: 'setter' | 'closer') => {
    setSavingTrack(track);
    setTrackError(null);
    try {
      await updateDoc(doc(db, `orgs/${session.orgId}/users/${session.user.uid}`), {
        trainingTrack: track,
      });
      setPickingTrack(false);
    } catch (err) {
      console.error('could not set the training track', err);
      setTrackError(describeWriteError(err));
    } finally {
      setSavingTrack(null);
    }
  };

  /**
   * An account with claims but no profile in this company (§7).
   *
   * Nothing works from here: the ladder has no progress to read, and every
   * write is refused because the rules require the document to already exist —
   * a browser may not create one, or a rep could bootstrap their own ladder.
   *
   * It has to be said here rather than left to the first failed write. The
   * refusal that comes back is `permission-denied`, which is what a stale
   * ruleset looks like too, so the app would confidently blame the rules for a
   * missing account and send whoever is reading it to the wrong place.
   */
  if (userMissing) {
    return (
      <main className="centered">
        <div className="card">
          <h1>This account has no profile here</h1>
          <p className="muted">
            {session.user.email} is signed in to <strong>{orgName ?? session.orgId}</strong>,
            but has no record in it. That record is written by provisioning, and
            nothing in the app can be done until it exists — the security rules
            do not let a browser create one.
          </p>
          <p className="muted small">
            An administrator fixes this by running <code>claims:provision</code> for
            this address against this company. Re-running it is safe: it never
            clears anything already passed.
          </p>
          <button className="ghost" onClick={signOutNow}>Sign out</button>
        </div>
      </main>
    );
  }

  if (rehearsing && (pickingTrack || savingTrack !== null || !user?.trainingTrack)) {
    return (
      <TrackPicker
        current={user?.trainingTrack ?? null}
        busy={savingTrack}
        error={trackError}
        onChoose={chooseTrack}
        onCancel={pickingTrack && !savingTrack ? () => setPickingTrack(false) : undefined}
      />
    );
  }

  if (user?.disabled) {
    return (
      <main className="centered">
        <div className="card">
          <h1>Account disabled</h1>
          <p className="muted">Speak to your manager.</p>
          <button className="ghost" onClick={signOutNow}>Sign out</button>
        </div>
      </main>
    );
  }

  if (openRung?.stage === 'summative' && !variantId) {
    return (
      <SummativeGate
        orgId={session.orgId}
        uid={session.user.uid}
        stage={openRung.key}
        // §13.6 — the grant is stamped with the track it was granted for, so a
        // setter's extra attempts do not follow the rep onto the closer ladder.
        granted={
          user?.extraSummativeAttemptsFor === openRung.key
            ? user?.extraSummativeAttempts ?? 0
            : 0
        }
        config={config}
        rehearsal={rehearsing}
        // §4.1 — only the variants authored for this rep's sales role. A setter
        // is never handed a closer's certification call.
        scenarios={content.filter((c) => openRung.contentIds.includes(c.id)) as never}
        onBegin={(choice) => { setVariantId(choice.scenarioId); setSeed(choice.seed); }}
        onDone={() => setOpenRung(null)}
      />
    );
  }

  if (openRung && openRung.kind === 'simulation' && (variantId ?? openRung.contentId)) {
    return (
      <SimulationRunner
        orgId={session.orgId}
        scenarioId={(variantId ?? openRung.contentId)!}
        config={config}
        uid={session.user.uid}
        salesRole={trainingRole(user)}
        seed={seed}
        onDone={() => { setOpenRung(null); setVariantId(null); setSeed(undefined); }}
      />
    );
  }

  // A knowledge rung shows its lessons first and the check on request; a drill
  // rung goes straight to repetition (§6.5, §19).
  const checkId = openRung?.kind === 'drill' ? openRung.contentId : inCheck;

  if (openRung && checkId) {
    return (
      <DrillRunner
        orgId={session.orgId}
        uid={session.user.uid}
        drillId={checkId}
        config={config}
        salesRole={trainingRole(user)}
        onDone={() => {
          if (openRung.kind === 'knowledge') setInCheck(null);
          else setOpenRung(null);
        }}
      />
    );
  }

  if (openRung && openRung.kind === 'knowledge') {
    return (
      <KnowledgeStage
        orgId={session.orgId}
        stage={openRung.stage}
        title={openRung.label}
        checkId={openRung.contentId}
        progress={openRung.progress}
        unitProgress={user?.unitProgress}
        byUnit={openRung.advanced}
        onTakeCheck={(drillId) => setInCheck(drillId)}
        onDone={() => { setOpenRung(null); setInCheck(null); }}
      />
    );
  }

  const open = (rung: Rung) => {
    if (rung.kind !== 'knowledge' && !rung.contentId) return;
    setOpenRung(rung);
    setInCheck(null);
    setSeed(undefined);
    // §14.3 — a practice rung with several authored calls serves one at random.
    // The certification rung decides at the gate instead, with the exclusions.
    setVariantId(
      rung.kind === 'simulation' && rung.stage !== 'summative'
        ? pickVariant(rung.contentIds)
        : null
    );
  };

  return (
    <div className="shell">
      {error && <p className="error banner" role="alert">{error}</p>}

      {/* Said on the screen somebody spends time on, not only at the gate: the
          whole point of letting a manager in here is that they see what a rep
          sees, and the one thing that must not be ambiguous is that none of it
          is going on their record. */}
      {rehearsing && (
        <p className="banner rehearsal">
          Rehearsal — you are walking the {user?.trainingTrack} ladder. Attempts
          are scored and reviewable, and earn no certification.
          {/* Said out loud, because an unlocked ladder is not the one a rep
              sees. Without this an admin reads "every stage open" as how the
              product behaves, and the gating is the product. */}
          {' Every stage is open to you; a rep unlocks them in order.'}
          {' '}
          <button className="linkish" onClick={() => setPickingTrack(true)}>
            Switch track
          </button>
        </p>
      )}

      <StageLadder rungs={rungs} user={user} onOpen={open} />
      <footer className="foot">
        {/* The company, not the build. A content version is something a
            manager quotes at me when a scenario is wrong; on a rep's screen it
            is a serial number where a name should be. */}
        <span className="muted small">{orgName ?? session.orgId}</span>
        {rehearsing
          ? <button className="ghost small" onClick={() => setTraining(false)}>Back to dashboard</button>
          : <button className="ghost small" onClick={signOutNow}>Sign out</button>}
      </footer>
    </div>
  );
}
